<template>
	<div class="c-variant-selector">
		<div class="c-variant-selector__price">
			{{ forSale && selectedVariantForSale ? selectedPrice : '&nbsp;' }}
		</div>

		<div class="c-variant-selector__controls" v-if="forSale">
			<template v-if="variants.length > 1">
				<div class="c-variant-selector-option" v-for="option in options">
					<label class="c-variant-selector-option__name screen-reader-text">{{ option.name }}:</label>

					<div class="c-variant-selector-option__choices">
						<select
							class="c-variant-selector-option__select"
							:name="option.name"
							:id="option.name"
							v-model="optionModels[option.name]"
						>
							<option v-for="value in option.values" :value="value">{{ value }}</option>
						</select>
					</div>
				</div>
			</template>

			<div class="c-variant-selector-quantity">
				<label class="c-variant-selector-quantity__name screen-reader-text" for="quantity"
					>{{ labels?.quantity }}:</label
				>

				<div class="c-variant-selector-quantity__choices">
					<button class="c-variant-selector-quantity__button" type="button" @click="decreaseQuantity">
						-
					</button>

					<select
						class="c-variant-selector-quantity__select"
						name="quantity"
						id="quantity"
						v-model="quantity"
					>
						<option v-for="quantity in selectedQuantity" :value="quantity">{{ quantity }}</option>
					</select>

					<button class="c-variant-selector-quantity__button" type="button" @click="increaseQuantity">
						+
					</button>
				</div>
			</div>
		</div>

		<button
			class="c-variant-selector__button"
			type="button"
			@click="addToCart"
			:disabled="!forSale || !selectedVariantForSale"
		>
			<span class="c-variant-selector__button-text">{{
				!forSale || !selectedVariantForSale ? labels?.outOfStock : labels?.addToCart
			}}</span>
		</button>
	</div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';

const optionModels = ref({});
const quantity = ref(1);

const props = defineProps({
	options: {
		type: Array,
		default: () => [],
	},
	variants: {
		type: Array,
		default: () => [],
	},
	labels: {
		type: Object,
		default: () => {},
	},
});

const increaseQuantity = () => {
	quantity.value = Math.min(quantity.value + 1, selectedQuantity.value);
};

const decreaseQuantity = () => {
	quantity.value = Math.max(quantity.value - 1, 1);
};

const forSale = computed(() => {
	return props.variants.some((variant) => {
		return variant.node.availableForSale;
	});
});

const selectedVariant = computed(() => {
	return props.variants.find((variant) => {
		return variant.node.selectedOptions.every((option) => {
			return optionModels.value[option.name] == option.value;
		});
	});
});

const selectedVariantForSale = computed(() => {
	return selectedVariant.value?.node?.availableForSale;
});

const selectedQuantity = computed(() => {
	return selectedVariant.value?.node?.quantityAvailable;
});

const selectedPrice = computed(() => {
	const price = selectedVariant.value?.node?.price;

	if (!price) {
		return '';
	}

	const locale = document.documentElement.lang ? document.documentElement.lang.replace(/_/g, '-') : 'en-US';

	return new Intl.NumberFormat(locale, {
		style: 'decimal',
		currency: price.currencyCode,
		minimumFractionDigits: 2, // Ensure 2 decimal places are shown
		maximumFractionDigits: 2,
	}).format(parseFloat(price.amount) * quantity.value);
});

watch(selectedVariant, (newVal) => {
	if (!quantity.value && selectedQuantity.value > 0) {
		quantity.value = 1;
		return;
	}

	if (quantity.value > selectedQuantity.value) {
		quantity.value = selectedQuantity.value;
		return;
	}
});

const addToCart = () => {
	const dataLayer = (window.dataLayer = window.dataLayer || []);
	dataLayer.push({
		event: 'add_to_cart',
		product_id: selectedVariant.value.node.id,
		product_title: selectedVariant.value.node.product.title,
		product_variant_id: selectedVariant.value.node.product.id,
		product_variant_title: selectedVariant.value.node.product.title,
		price: parseFloat(selectedVariant.value.node.price.amount) * quantity.value,
		currency: selectedVariant.value.node.price.currencyCode,
		quantity: quantity.value,
	});

	wp.hooks.doAction('do.addToCart', {
		lines: [
			{
				quantity: quantity.value,
				merchandiseId: selectedVariant.value.node.id,
			},
		],
		extras: {
			openCartAfterAdding: true,
		},
	});
};

onMounted(() => {
	props.options.forEach((option) => {
		optionModels.value[option.name] = option.values[0];
	});
});
</script>

<style lang="scss" scoped></style>
